import type { CustomTooltipProps } from 'ag-grid-react';
import { FieldMessage } from 'apiConnector/secondary-market/batch/base';
import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { EditableFieldInfo } from 'components/shared/Blotter/Grid/blotter.model';
import { useMemo } from 'react';
import { useGetHigherColor } from 'routes/secondary-market-batch/grid/hooks/use-get-higher-color';
import { useMapMessage } from 'routes/secondary-market-batch/grid/hooks/use-map-message';
import { Context } from 'routes/secondary-market-batch/grid/types';

type MessageTooltipValue = {
  validationMessages?: FieldMessage[];
  editableState?: EditableFieldInfo;
};

function useMapMessages(orderType: OrderType, messages?: FieldMessage[]) {
  const mapMessage = useMapMessage(orderType);
  return useMemo(() => (messages ?? []).map(validation => mapMessage(validation)), [mapMessage, messages]);
}

export function MessageTooltip({
  value,
  context: { orderType },
}: CustomTooltipProps<unknown, MessageTooltipValue, Context>) {
  const { validationMessages: valMessages, editableState } = value ?? {};
  const validationMessages = useMapMessages(orderType, valMessages);
  const color = useGetHigherColor(valMessages, editableState);

  const editableStateMessages = [
    ...(editableState?.errors ? [editableState.errors] : []),
    ...(editableState?.warnings ? [editableState.warnings] : []),
  ];

  const messages = validationMessages.concat(editableStateMessages);

  if (messages.length === 0) {
    return null;
  }

  return (
    <div className={`p-2 rounded border bg-${color}`}>
      {messages.map(message => (
        <div key={message}>{message}</div>
      ))}
    </div>
  );
}
